import React, { useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/ui.duck';

import { Page, LayoutSingleColumn, IconCheckmark } from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import ContactUsForm from './ContactUsForm/ContactUsForm';

import css from './ContactUsPage.module.css';

const CONTACT_EMAIL_RECIPIENT = 'info@gearlyusa.com';

/**
 * Build a `mailto:` URL from the contact form values.
 * The user's mail client opens with subject prefilled and the body containing
 * the sender's name, email, and message — so the recipient can reply even if
 * the user's mail client sends from a different account than the one entered.
 *
 * @param {Object} values raw form values
 * @param {function} formatMessage `intl.formatMessage`
 * @returns {string} mailto URL
 */
const buildContactMailtoUrl = (values, formatMessage) => {
  const { name = '', email = '', subject, message = '' } = values || {};

  const subjectLine = subject
    ? formatMessage({ id: `ContactUsForm.subjectOption.${subject}` })
    : formatMessage({ id: 'ContactUsPage.defaultEmailSubject' });

  const trimmedName = name.trim();
  const trimmedEmail = email.trim();
  const fromLine = trimmedName
    ? `From: ${trimmedName}${trimmedEmail ? ` <${trimmedEmail}>` : ''}`
    : trimmedEmail
    ? `From: ${trimmedEmail}`
    : '';

  const bodyLines = [fromLine, '', message].filter(line => line !== null && line !== undefined);
  const body = bodyLines.join('\n');

  const query = `subject=${encodeURIComponent(subjectLine)}&body=${encodeURIComponent(body)}`;
  return `mailto:${CONTACT_EMAIL_RECIPIENT}?${query}`;
};

/**
 * ContactUsPage shows a static "Contact Us" page with a Final Form-based
 * contact form. On submit, the user's email client is opened with a
 * pre-filled `mailto:` link addressed to the marketplace inbox.
 *
 * @param {Object} props
 * @param {boolean} props.scrollingDisabled
 * @returns {JSX.Element} contact us page
 */
export const ContactUsPageComponent = props => {
  const { scrollingDisabled } = props;
  const intl = useIntl();

  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = values => {
    const mailtoUrl = buildContactMailtoUrl(values, intl.formatMessage);
    if (typeof window !== 'undefined') {
      // Opens the user's default email client with subject/body pre-filled.
      // `mailto:` does not navigate the current page in modern browsers.
      window.location.href = mailtoUrl;
    }
    setSubmitted(true);
  };

  const handleFormChange = () => {
    if (submitted) {
      setSubmitted(false);
    }
  };

  return (
    <Page
      title={intl.formatMessage({ id: 'ContactUsPage.schemaTitle' })}
      description={intl.formatMessage({ id: 'ContactUsPage.schemaDescription' })}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn
        mainColumnClassName={css.layoutWrapperMain}
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
      >
        <section className={css.hero}>
          <div className={css.heroInner}>
            <h1 className={css.heroTitle}>
              <FormattedMessage id="ContactUsPage.heroTitle" />
            </h1>
            <p className={css.heroSubtext}>
              <FormattedMessage id="ContactUsPage.heroSubtext" />
            </p>
          </div>
        </section>

        <section className={css.formSection}>
          <div className={css.formCard}>
            <h2 className={css.formHeadline}>
              <FormattedMessage id="ContactUsPage.formHeadline" />
            </h2>

            {submitted ? (
              <div className={css.successMessage} role="status" aria-live="polite">
                <IconCheckmark className={css.successIcon} size="small" />
                <p className={css.successText}>
                  <FormattedMessage id="ContactUsPage.successMessage" />
                </p>
              </div>
            ) : null}

            <ContactUsForm onSubmit={handleSubmit} onChange={handleFormChange} />
          </div>
        </section>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const ContactUsPage = compose(connect(mapStateToProps))(ContactUsPageComponent);

export default ContactUsPage;
